import { msalConfig } from '@api/msalConfig';
import { PublicClientApplication } from '@azure/msal-browser';
import useAuthStore from '@store/authStore';
import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePageHeader } from '../../../context/PageHeaderContext';

const titles: { [key: string]: string } = {
  '/': 'Dashboard',
  '/reports': 'Reports',
  '/accounts': 'Accounts',
  '/insights-and-analytics': 'Insights & analytics',
  // help: 'Help, support & ideas',
  '/settings/manage-users': 'Manage Clevr360 users',
  '/settings/manage-connected-services': 'Connected services',
  '/settings/manage-operating-services': 'Manage operating services',
  '/my-profile': 'User profile',
  '/settings': 'Settings',
};

const getTitle = (pathname: string) => {
  if (pathname === '/') return titles['/'];

  const sortedRoutes = Object.keys(titles).sort((a, b) => b.length - a.length);
  for (const route of sortedRoutes) {
    if (route !== '/' && pathname.startsWith(route)) {
      return titles[route];
    }
  }
  return '';
};

type Props = {
  headerActions?: React.ReactNode;
  customTitle?: string | null;
  testId?: string;
};

const msalInstance = new PublicClientApplication(msalConfig);
export const PageHeader: React.FC<Props> = ({ headerActions, customTitle, testId }) => {
  const authStore = useAuthStore();
  const location = useLocation();

  const { customTitle: contextTitle } = usePageHeader(); // Get title from context
  const title = getTitle(location.pathname);
  
  // null = explicitly hide, undefined = use default
  const displayTitle = customTitle !== undefined ? customTitle : (contextTitle !== undefined ? contextTitle : title);

  const titleTestId = testId ? `${testId}-title` : 'page-header-title';
  


  // Read parent origin from sessionStorage (set by ImpersonateLogin from the URL param) or
  // fall back to the window global. Defaults to localhost for local development.
  const parentOrigin = sessionStorage.getItem('parentOrigin') || window.admin360Url || 'http://localhost:4000';

  const onRecievedMessage = useCallback(function (event: MessageEvent) {
    // check the message source origin as a security measure
    if (event.origin !== parentOrigin) {
      return;
    }
    if (event.data === 'logout') {
      msalInstance.clearCache();
      // Send object format to match Admin.Web's listener: event.data?.type === 'logout-complete'
      window.parent.postMessage({ type: 'logout-complete' }, parentOrigin);
    }
  }, [parentOrigin]);

  useEffect(function () {
    window.addEventListener("message", onRecievedMessage);
    return function () {
      window.removeEventListener("message", onRecievedMessage);
    };
  }, [onRecievedMessage]);

  useEffect(() => {
    window.addEventListener('unload', handleTabClosing)
    return () => {
      window.removeEventListener('unload', handleTabClosing)
    }
  })

  const handleTabClosing = () => {
    // If we are connecting through Admin360 we need to clear the athentication cache (soft logout) on tab close
    const orgToken = sessionStorage.getItem("organizationToken");
    if (orgToken) {
      msalInstance.clearCache();
    }
  }

  return (
    <>
  {displayTitle && (
        <header className="mb-5 flex items-center justify-between">
          <h1 className="tracking[-0.02em] text-32 font-normal leading-9 text-interfaceColor-100 sm:leading-[48px] md:text-[48px] lg:leading-[72px]" data-testid={titleTestId}>
            {!customTitle && !contextTitle && title === titles['/']
              ? `Hi, ${authStore.user?.fullName.split(' ')[0] ?? ''}`
              : displayTitle}
          </h1>

          {headerActions && headerActions}
        </header>
    )}
    </>
  );
};